import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Atom, BrainCircuit, CircleDot, Download, Expand, Flame, Orbit, Save, Sparkles, Trash2, WandSparkles, Wind } from "lucide-react";
import { audioEngine } from "../services/audioEngine";
import { generatePipelineNodes, lookupCorrespondences, refineIntent } from "../services/lunaPipeline";
import { useSanctumStore } from "../stores/useSanctumStore";
import type { Binding, InkStyle, Planet, RenderMode, SigilDesign, SigilNode } from "../types";
import { createSigilSvg, DEFAULT_RULES, downloadText, generateWheelNodes, localCorrespondence, reduceIntent, type ReductionRules } from "../utils/sigil";
import { SigilCanvas } from "./SigilCanvas";

const PLANETS: Planet[] = ["wheel", "saturn", "jupiter", "mars", "sun", "venus", "mercury", "moon"];
const INKS: InkStyle[] = ["gold", "neon", "chalk", "blood"];
const BINDINGS: Binding[] = ["none", "circle", "celestial", "runic"];

export function SigilModule() {
  const saveSigil = useSanctumStore((state) => state.saveSigil);
  const addRitualLog = useSanctumStore((state) => state.addRitualLog);
  const [intent, setIntent] = useState("");
  const [assistant, setAssistant] = useState(true);
  const [rules, setRules] = useState<ReductionRules>(DEFAULT_RULES);
  const [planet, setPlanet] = useState<Planet>("wheel");
  const [mode, setMode] = useState<RenderMode>("rose");
  const [ink, setInk] = useState<InkStyle>("gold");
  const [binding, setBinding] = useState<Binding>("circle");
  const [stroke, setStroke] = useState(5);
  const [glow, setGlow] = useState(13);
  const [symmetry, setSymmetry] = useState(1);
  const [tension, setTension] = useState(.35);
  const [remoteNodes, setRemoteNodes] = useState<SigilNode[] | null>(null);
  const [working, setWorking] = useState(false);
  const [signal, setSignal] = useState<"luna" | "local">("local");
  const [chamber, setChamber] = useState(false);
  const [holding, setHolding] = useState(false);
  const [charge, setCharge] = useState(0);
  const [phase, setPhase] = useState<"normal" | "burst" | "fire">("normal");
  const [bpm, setBpm] = useState(6);
  const completed = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const reduced = useMemo(() => reduceIntent(intent, rules), [intent, rules]);
  const correspondence = useMemo(() => localCorrespondence(intent), [intent]);
  const nodes = useMemo(() => remoteNodes || generateWheelNodes(reduced, planet), [planet, reduced, remoteNodes]);
  useEffect(() => setRemoteNodes(null), [intent, planet, rules]);

  const distill = async () => {
    if (!intent.trim()) return;
    setWorking(true);
    if (assistant) {
      const result = await refineIntent(intent, rules);
      setIntent(result.result.refined);
      setSignal(result.source);
      const correspondenceResult = await lookupCorrespondences(result.result.refined);
      setPlanet(correspondenceResult.result.planet);
      setSignal(correspondenceResult.source);
    }
    setWorking(false);
  };

  const mapWithLuna = async () => {
    if (!reduced) return;
    setWorking(true);
    const result = await generatePipelineNodes(intent, reduced, planet, rules);
    if (Array.isArray(result.result.nodes) && result.result.nodes.length) setRemoteNodes(result.result.nodes);
    if (planet === "wheel") setPlanet(result.result.planet);
    setSignal(result.source); setWorking(false);
  };

  const svg = () => createSigilSvg(nodes, { stroke, glow, symmetry, ink, binding, mode, tension });
  const makeDesign = (): SigilDesign => ({
    id: crypto.randomUUID(), intent: intent.trim(), reduced, planet,
    element: correspondence.element, mode, ink, binding, stroke, glow, symmetry, tension,
    svg: svg(), createdAt: Date.now(), charges: charge >= 100 ? [{ at: Date.now(), method: "gnosis", energy: 100 }] : [],
  });
  const save = () => { if (intent.trim() && reduced) saveSigil(makeDesign()); };

  const exportPng = () => canvasRef.current?.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob); const anchor = document.createElement("a");
    anchor.href = url; anchor.download = `sigil-${reduced || "untitled"}.png`; anchor.click(); URL.revokeObjectURL(url);
  }, "image/png");

  const openChamber = () => {
    if (!reduced) return;
    completed.current = false; setCharge(0); setPhase("normal"); setChamber(true);
    void document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const closeChamber = () => {
    setHolding(false); audioEngine.stopCharge(); setChamber(false);
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
  };

  useEffect(() => {
    if (!holding || !chamber || completed.current) return;
    const interval = window.setInterval(() => setCharge((current) => Math.min(100, current + 1.4)), 42);
    return () => window.clearInterval(interval);
  }, [chamber, holding]);
  useEffect(() => {
    if (!chamber) return;
    audioEngine.setCharge(charge);
    if (charge >= 100 && !completed.current) {
      completed.current = true; setHolding(false); setPhase("burst"); audioEngine.burst();
      addRitualLog("charge", `Sigil ${reduced} reached full charge`);
    }
  }, [addRitualLog, chamber, charge, reduced]);
  useEffect(() => {
    if (!chamber) return;
    const down = (event: KeyboardEvent) => {
      if (event.code === "Space" && !(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) { event.preventDefault(); setHolding(true); }
    };
    const up = (event: KeyboardEvent) => { if (event.code === "Space") setHolding(false); };
    const pointerUp = () => setHolding(false);
    window.addEventListener("keydown", down); window.addEventListener("keyup", up); window.addEventListener("pointerup", pointerUp);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("pointerup", pointerUp); };
  }, [chamber]);

  const forget = (fire = false) => {
    if (fire) { setPhase("fire"); audioEngine.paperBurn(); }
    addRitualLog("banish", `Sigil ${reduced} was ${fire ? "burned" : "released and forgotten"}`);
    window.setTimeout(() => { setIntent(""); setRemoteNodes(null); closeChamber(); }, fire ? 1200 : 50);
  };

  return <>
    <div className="module-grid sigil-layout">
      <section className="space-y-5">
        <div className="panel p-5">
          <div className="step-label"><span>01</span> Statement & reduction</div>
          <textarea className="field mt-4 min-h-32 resize-y text-base" value={intent} onChange={(event) => setIntent(event.target.value)} placeholder="MY WILL IS TO MASTER CONCURRENT DATA FLOWS" maxLength={1000} />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button className={`toggle-chip ${assistant ? "active" : ""}`} onClick={() => setAssistant((value) => !value)}><BrainCircuit size={14}/> Luna intent assistant</button>
            <button className="primary-button" disabled={!intent.trim() || working} onClick={distill}><WandSparkles size={15}/>{working ? "Distilling…" : "Distill intention"}</button>
          </div>
          <div className="rule-grid mt-5">
            {([
              ["removeVowels", "Strip vowels"], ["removeDuplicates", "Remove duplicates"], ["keepY", "Keep Y"],
            ] as const).map(([key, label]) => <label key={key}><input type="checkbox" checked={rules[key]} onChange={(event) => setRules({ ...rules, [key]: event.target.checked })}/><span>{label}</span></label>)}
          </div>
          <div className="reduction-readout"><span>REDUCED LETTER SET</span><strong>{reduced || "—"}</strong></div>
        </div>

        <div className="panel p-5">
          <div className="step-label"><span>02</span> Geometry & material</div>
          <div className="control-grid mt-5">
            <label><span>Render mode</span><select value={mode} onChange={(event) => setMode(event.target.value as RenderMode)}><option value="rose">Planetary rose</option><option value="freeform">Freeform harmonizer</option></select></label>
            <label><span>Planetary current</span><select value={planet} onChange={(event) => setPlanet(event.target.value as Planet)}>{PLANETS.map((value) => <option key={value}>{value}</option>)}</select></label>
            <label><span>Ink</span><select value={ink} onChange={(event) => setInk(event.target.value as InkStyle)}>{INKS.map((value) => <option key={value}>{value}</option>)}</select></label>
            <label><span>Binding</span><select value={binding} onChange={(event) => setBinding(event.target.value as Binding)}>{BINDINGS.map((value) => <option key={value}>{value}</option>)}</select></label>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Range label="Stroke" value={stroke} min={1} max={14} step={1} onChange={setStroke}/>
            <Range label="Glow" value={glow} min={0} max={40} step={1} onChange={setGlow}/>
            <Range label="Symmetry" value={symmetry} min={1} max={8} step={1} onChange={setSymmetry}/>
            <Range label="Curve tension" value={tension} min={0} max={1} step={.05} onChange={setTension}/>
          </div>
          <button className="secondary-button mt-5" onClick={mapWithLuna} disabled={!reduced || working}><Orbit size={15}/> Map nodes with Luna</button>
        </div>

        <div className="panel p-5">
          <div className="step-label"><span>03</span> Charge, keep, or banish</div>
          <p className="mt-3 text-sm leading-6 text-bone/55">The chamber responds to a held pointer or spacebar. At full charge, choose whether the vector enters your grimoire or disappears.</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button className="primary-button" onClick={openChamber} disabled={!reduced}><Expand size={15}/> Enter chamber</button>
            <button className="secondary-button" onClick={save} disabled={!reduced}><Save size={15}/> Save now</button>
            <button className="secondary-button" onClick={() => downloadText(`sigil-${reduced || "untitled"}.svg`, svg(), "image/svg+xml")} disabled={!reduced}><Download size={15}/> Export SVG</button>
            <button className="secondary-button" onClick={exportPng} disabled={!reduced}><Download size={15}/> Export PNG</button>
          </div>
        </div>
      </section>

      <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">
        <div className="panel overflow-hidden p-1 shadow-gold"><SigilCanvas ref={canvasRef} nodes={nodes} stroke={stroke} glow={glow} symmetry={symmetry} tension={tension} ink={ink} binding={binding} mode={mode} labels /></div>
        <div className="panel grid grid-cols-3 divide-x divide-white/5 p-4 text-center">
          <Meta icon={<Atom size={15}/>} label="Current" value={planet}/><Meta icon={<Wind size={15}/>} label="Element" value={correspondence.element}/><Meta icon={<Sparkles size={15}/>} label="Signal" value={signal}/>
        </div>
        <div className="border-l border-brass/30 px-4 text-xs leading-5 text-bone/45">{correspondence.rationale} Correspondences are creative ritual symbolism, not predictions.</div>
      </aside>
    </div>

    <AnimatePresence>
      {chamber && <motion.div className="charging-chamber" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <motion.div className="chamber-aura" animate={{ scale: [1, 1.06, 1], opacity: [.28, .58, .28] }} transition={{ duration: 60 / bpm, repeat: Infinity, ease: "easeInOut" }}/>
        <div className={`chamber-canvas phase-${phase}`}><SigilCanvas nodes={nodes} stroke={stroke + charge * .018} glow={glow + charge * .16} symmetry={symmetry} tension={tension} ink={ink} binding={binding} mode={mode} charge={charge} phase={phase}/></div>
        <div className="relative z-10 w-full max-w-xl px-6 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[.35em] text-bone/45">Charging chamber · {bpm} breaths/min</div>
          <div className="charge-track mt-4"><motion.div animate={{ width: `${charge}%` }}/></div>
          <div className="mt-2 font-mono text-xl text-bone">{Math.round(charge)}%</div>
          {charge < 100 ? <>
            <button className="hold-button mt-5" onPointerDown={() => setHolding(true)} onPointerLeave={() => setHolding(false)}><CircleDot size={18}/>{holding ? "Hold the current" : "Hold mouse or space"}</button>
            <input aria-label="Breathing rate" className="mt-5 w-48 accent-brass" type="range" min="4" max="18" value={bpm} onChange={(event) => setBpm(Number(event.target.value))}/>
            <button className="block mx-auto mt-4 text-xs uppercase tracking-widest text-bone/40 hover:text-bone" onClick={closeChamber}>Leave chamber</button>
          </> : <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
            <p className="font-display text-2xl text-bone">The form has broken open.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button className="primary-button" onClick={() => { save(); closeChamber(); }}><Save size={15}/> Keep in grimoire</button>
              <button className="secondary-button" onClick={() => forget(false)}><Trash2 size={15}/> Forget silently</button>
              <button className="danger-button" onClick={() => forget(true)}><Flame size={15}/> Disintegrate</button>
            </div>
          </motion.div>}
        </div>
      </motion.div>}
    </AnimatePresence>
  </>;
}

function Range({ label, value, min, max, step, onChange }: { label: string; value: number; min: number; max: number; step: number; onChange: (value: number) => void }) {
  return <label className="range-control"><span>{label}<b>{Number.isInteger(step) ? value : value.toFixed(2)}</b></span><input type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))}/></label>;
}

function Meta({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return <div className="px-2"><span className="mx-auto flex justify-center text-brass">{icon}</span><small className="mt-1 block text-[9px] uppercase tracking-widest text-bone/35">{label}</small><b className="mt-1 block font-mono text-xs capitalize text-bone/75">{value}</b></div>;
}
